import { 
  Box, 
  Chip,
  Typography, 
  Avatar, 
  Divider, 
  Container, 
  Grid2,
  List, 
  ListItem, 
  ListItemText, 
  alpha,
  Button,
  Snackbar,
  Alert,
  Card,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  CircularProgress,
  IconButton,
  Paper,
} from '@mui/material';
import { useState, useEffect } from 'react';
import EmailIcon from '@mui/icons-material/Email';
import AccountCircleIcon from '@mui/icons-material/AccountCircle';
import HomeWorkIcon from '@mui/icons-material/HomeWork';
import DeleteForeverIcon from '@mui/icons-material/DeleteForever';
import EditIcon from '@mui/icons-material/Edit';
import HomeIcon from '@mui/icons-material/Home';
import StarIcon from '@mui/icons-material/Star';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import PersonIcon from '@mui/icons-material/Person';
import BusinessIcon from '@mui/icons-material/Business';
import PhoneIcon from '@mui/icons-material/Phone';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import SportsEsportsIcon from '@mui/icons-material/SportsEsports';
import BasePage from './BasePage';
import { useAuth } from '../contexts/AuthContext';
import { useClient } from '../contexts/ClientContext';
import { useGlobalChatContext } from '../contexts/GlobalChatContext';
import { useNavigate } from 'react-router-dom';
import HomePreferencesDialog from '../components/HomePreferencesDialog';
import PreferencesWizardChat from '../components/PreferencesWizardChat';
import ThisOrThatGame from '../components/ThisOrThatGame';
import { analytics } from '../services/analytics';
import { useAnalytics } from '../hooks/useAnalytics';

const formatAuthType = (authType: string): string => {
  if (!authType) return "Unknown";
  
  switch (authType.toLowerCase()) {
    case 'google':
      return 'Google Account';
    case 'email':
      return 'Email and Pin';
    default:
      return authType.charAt(0).toUpperCase() + authType.slice(1);
  }
};

function Profile() {
  const { user, logout } = useAuth();
  const { hasAgent, agent } = useClient();
  const { setHideFab } = useGlobalChatContext();
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const navigate = useNavigate();
    
  // Keep the following
  const [preferencesDialogOpen, setPreferencesDialogOpen] = useState(false);
  const [preferencesWizardOpen, setPreferencesWizardOpen] = useState(false);
  const [thisOrThatGameOpen, setThisOrThatGameOpen] = useState(false);
  const [preferencesMessage, setPreferencesMessage] = useState({ type: 'success', text: '' });
  const [displayedPreferences, setDisplayedPreferences] = useState(user?.home_preferences || '');

  // Log settings_open analytics event
  useAnalytics(
    () => {
      if (user?.uuid) {
        analytics.logSettingsOpen(user.uuid);
      }
    },
    [user?.uuid]
  );

  useEffect(() => {
    if (user) {
      setDisplayedPreferences(user.home_preferences || '');
    }
  }, [user]);

  // Hide the global chat FAB when the game dialog is open
  useEffect(() => {
    setHideFab(thisOrThatGameOpen);
  }, [thisOrThatGameOpen, setHideFab]);

  if (!user) {
    return (
      <BasePage>
        <Container maxWidth="md" sx={{ py: 4 }}>
          <Typography variant="h4">Loading profile...</Typography>
        </Container>
      </BasePage>
    );
  }

  const handleDeleteAccount = async () => {
    setIsDeleting(true);
    setErrorMessage('');
    
    try {
      const response = await fetch('/api/account', {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json'
        }
      });
      
      const data = await response.json();
      
      if (data.success) {
        // Account deleted successfully
        setDeleteDialogOpen(false);
        logout();
        navigate('/');
      } else {
        setErrorMessage(data.error || 'Failed to delete account');
      }
    } catch (error) {
      setErrorMessage('An error occurred while deleting your account');
      console.error('Delete account error:', error);
    } finally {
      setIsDeleting(false);
    }
  };

  // Replace updateHomePreferences with this simpler version
  const updateHomePreferences = async (newPreferences: string) => {
    try {
      const response = await fetch('/api/account/home_preferences', {
        method: 'PATCH',
        body: newPreferences
      });
      
      if (response.ok) {
        // Update the displayed preferences immediately
        setDisplayedPreferences(newPreferences);
        
        // Close dialog and show success message
        setPreferencesDialogOpen(false);
        setPreferencesMessage({ type: 'success', text: 'Home preferences updated successfully' });
      } else {
        const data = await response.json();
        setPreferencesMessage({ type: 'error', text: data.error || 'Failed to update preferences' });
      }
    } catch (error) {
      setPreferencesMessage({ 
        type: 'error', 
        text: error instanceof Error ? error.message : 'An error occurred' 
      });
    }
  };

  const handleUpgradeToPremium = () => {
    // Navigate to the pricing page
    navigate('/pricing');
  };

  // Keep handleOpenPreferencesDialog
  const handleOpenPreferencesDialog = () => {
    setPreferencesDialogOpen(true);
  };

  const handleOpenPreferencesWizard = () => {
    setPreferencesWizardOpen(true);
  };

  const handleOpenThisOrThatGame = () => {
    setThisOrThatGameOpen(true);
  };

  const handleGameComplete = async (preferences: string) => {
    // Save the preferences generated by the game
    try {
      await updateHomePreferences(preferences);
      setThisOrThatGameOpen(false);
    } catch (error) {
      // If saving fails, show error with the preferences text so user can copy it
      const errorMsg = error instanceof Error ? error.message : 'Failed to save preferences';
      setPreferencesMessage({
        type: 'error',
        text: `${errorMsg}. Your preferences were: "${preferences.substring(0, 100)}${preferences.length > 100 ? '...' : ''}". Please try again or save manually.`
      });
      console.error('Error saving game preferences:', error);
    }
  };

  const handleWizardComplete = async (preferences: string) => {
    // Save the preferences generated by the wizard
    try {
      await updateHomePreferences(preferences);
    } catch (error) {
      // If saving fails, show error with the preferences text so user can copy it
      const errorMsg = error instanceof Error ? error.message : 'Failed to save preferences';
      setPreferencesMessage({
        type: 'error',
        text: `${errorMsg}. Your preferences were: "${preferences.substring(0, 100)}${preferences.length > 100 ? '...' : ''}". Please try again or save manually.`
      });
      console.error('Error saving wizard preferences:', error);
    }
  };

  return (
    <BasePage>
      <Container maxWidth="md" sx={{ py: 4 }}>
        <Card 
          sx={{ 
            p: 3, 
            borderRadius: 1,
            boxShadow: theme => `0 8px 24px ${alpha(theme.palette.mode === 'dark' ? theme.palette.common.black : theme.palette.grey[300], 0.15)}`
          }}
        >
          <Grid2 container spacing={4}>
            {/* Profile header with avatar */}
            <Grid2 size={12}>
              <Box sx={{ 
                display: 'flex', 
                flexDirection: { xs: 'column', sm: 'row' }, 
                alignItems: { xs: 'center', sm: 'flex-start' },
                gap: 3
              }}>
                <Avatar 
                  src={user.picture} 
                  alt={user.name || user.username} 
                  sx={{ 
                    width: 120, 
                    height: 120, 
                    border: theme => `4px solid ${alpha(theme.palette.primary.main, theme.palette.mode === 'dark' ? 0.5 : 0.7)}`,
                    boxShadow: theme => `0 4px 20px ${alpha(theme.palette.common.black, theme.palette.mode === 'dark' ? 0.4 : 0.1)}`
                  }} 
                />
                
                <Box sx={{ textAlign: { xs: 'center', sm: 'left' } }}>
                  <Typography variant="h4" fontWeight="bold" gutterBottom>
                    {user.name || user.username}
                  </Typography>
                  <Typography 
                    variant="body1" 
                    color="text.secondary" 
                    sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}
                  >
                    <EmailIcon fontSize="small" />
                    {user.email}
                  </Typography>
                  <Typography 
                    variant="body2" 
                    sx={{ 
                      mt: 2,
                      color: 'success.main',
                      fontWeight: 'medium',
                      display: 'flex',
                      alignItems: 'center',
                      gap: 0.5
                    }}
                  >
                    <Box component="span" sx={{ 
                      width: 8, 
                      height: 8, 
                      bgcolor: 'success.main', 
                      borderRadius: '50%',
                      display: 'inline-block'
                    }} />
                    Active Account
                  </Typography>
                </Box>
              </Box>
            </Grid2>

            <Grid2 size={12}>
              <Divider />
            </Grid2>

            {/* Account Details */}
            <Grid2 size={{xs:12, md:6}}>
              <Typography variant="h6" gutterBottom sx={{ 
                display: 'flex', 
                alignItems: 'center', 
                gap: 1, 
                color: 'primary.main'
              }}>
                <AccountCircleIcon />
                Account Details
              </Typography>
              
              <List sx={{ 
                bgcolor: theme => alpha(
                  theme.palette.mode === 'dark' ? theme.palette.grey[800] : theme.palette.grey[100], 
                  theme.palette.mode === 'dark' ? 0.4 : 0.5
                ), 
                borderRadius: 1, // Less rounded corners
                border: theme => `1px solid ${alpha(
                  theme.palette.mode === 'dark' ? theme.palette.grey[700] : theme.palette.grey[300], 
                  0.5
                )}`
              }}>
                <ListItem>
                  <ListItemText primary="Account ID" secondary={user.sub || 'Not available'} />
                </ListItem>
                <Divider component="li" />
                <ListItem>
                  <ListItemText 
                    primary="Account Type" 
                    secondary={user.subscription_tier === 'pro' ? "Pro Tier" : "Free Tier"} 
                  />
                </ListItem>
                <Divider component="li" />
                <ListItem>
                  <ListItemText 
                    primary="Created" 
                    secondary={
                      user.created_at 
                        ? new Date(user.created_at).toLocaleDateString('en-US', {
                            year: 'numeric',
                            month: 'long',
                            day: 'numeric',
                            timeZone: 'UTC'
                          })
                        : "Account information synced with Google"
                    } 
                  />
                </ListItem>
                <Divider component="li" />
                <ListItem>
                  <ListItemText 
                    primary="Authentication Method" 
                    secondary={formatAuthType(user.auth_type)}
                  />
                </ListItem>
              </List>
            </Grid2>

            {/* App Usage */}
            <Grid2 size={{xs:12, md:6}}>
              <Typography variant="h6" gutterBottom sx={{ 
                display: 'flex', 
                alignItems: 'center', 
                gap: 1, 
                color: 'secondary.main'
              }}>
                <HomeWorkIcon />
                CribCritic Activity
              </Typography>
              
              <List sx={{ 
                bgcolor: theme => alpha(
                  theme.palette.mode === 'dark' ? theme.palette.grey[800] : theme.palette.grey[100], 
                  theme.palette.mode === 'dark' ? 0.4 : 0.5
                ), 
                borderRadius: 1, // Less rounded corners
                border: theme => `1px solid ${alpha(
                  theme.palette.mode === 'dark' ? theme.palette.grey[700] : theme.palette.grey[300], 
                  0.5
                )}`
              }}>
                <ListItem>
                  <ListItemText primary="Homes" secondary="View and manage your saved homes" />
                </ListItem>
                <Divider component="li" />
                <ListItem>
                  <ListItemText primary="Account Settings" secondary="Update notification preferences" />
                </ListItem>
              </List>
            </Grid2>

            {/* Home Preferences Section */}
            <Grid2 size={12} sx={{ mt: 3 }}>
              <Box sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                flexWrap: 'wrap',
                gap: 1,
                mb: 1
              }}>
                <Typography variant="h6" sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1,
                  color: 'primary.main',
                  m: 0
                }}>
                  <HomeIcon />
                  Home Preferences
                </Typography>
                <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                  <Button
                    size="small"
                    variant="contained"
                    color="secondary"
                    startIcon={<AutoAwesomeIcon />}
                    onClick={handleOpenPreferencesWizard}
                    sx={{
                      textTransform: 'none',
                      borderRadius: 2,
                      fontWeight: 500
                    }}
                  >
                    AI Wizard
                  </Button>
                  <Button
                    size="small"
                    variant="outlined"
                    color="primary"
                    startIcon={<SportsEsportsIcon />}
                    onClick={handleOpenThisOrThatGame}
                    sx={{
                      textTransform: 'none',
                      borderRadius: 2,
                      fontWeight: 500
                    }}
                  >
                    This or That
                  </Button>
                  <IconButton
                    size="small"
                    color="primary"
                    onClick={handleOpenPreferencesDialog}
                    aria-label="Edit home preferences"
                    title="Edit preferences manually"
                  >
                    <EditIcon fontSize="small" />
                  </IconButton>
                </Box>
              </Box>

              <Paper sx={{
                p: 2,
                bgcolor: theme => alpha(
                  theme.palette.mode === 'dark' ? theme.palette.grey[800] : theme.palette.grey[100],
                  theme.palette.mode === 'dark' ? 0.4 : 0.5
                ),
                borderRadius: 1,
                border: theme => `1px solid ${alpha(
                  theme.palette.mode === 'dark' ? theme.palette.grey[700] : theme.palette.grey[300],
                  0.5
                )}`,
                minHeight: '100px'
              }}>
                {displayedPreferences ? (
                  <Box sx={{
                    display: '-webkit-box',
                    WebkitLineClamp: 3,
                    WebkitBoxOrient: 'vertical',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'pre-wrap',
                    textAlign: 'left'
                  }}>
                    {displayedPreferences}
                  </Box>
                ) : (
                  <Box sx={{ textAlign: 'center' }}>
                    <Typography color="text.secondary" sx={{ fontStyle: 'italic', mb: 2 }}>
                      No home preferences set yet.
                    </Typography>
                    <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center', flexWrap: 'wrap' }}>
                      <Button
                        variant="outlined"
                        color="secondary"
                        startIcon={<AutoAwesomeIcon />}
                        onClick={handleOpenPreferencesWizard}
                        sx={{
                          textTransform: 'none',
                          borderRadius: 2
                        }}
                      >
                        Set Up with AI Wizard
                      </Button>
                      <Button
                        variant="outlined"
                        color="primary"
                        startIcon={<SportsEsportsIcon />}
                        onClick={handleOpenThisOrThatGame}
                        sx={{
                          textTransform: 'none',
                          borderRadius: 2
                        }}
                      >
                        Play This or That
                      </Button>
                    </Box>
                  </Box>
                )}
              </Paper>
            </Grid2>

            {/* Conditional Banner Section */}
            {hasAgent && agent ? (
              // Agent Recognition Banner
              <Grid2 size={12} sx={{ mt: 3 }}>
                <Paper 
                  sx={{ 
                    p: { xs: 2, sm: 3 },
                    borderRadius: 1,
                    background: theme => `linear-gradient(145deg, 
                      ${alpha(theme.palette.primary.main, 0.1)} 0%, 
                      ${alpha(theme.palette.secondary.main, 0.15)} 100%)`,
                    border: theme => `1px solid ${alpha(theme.palette.primary.main, 0.3)}`,
                    position: 'relative',
                    overflow: 'hidden'
                  }}
                >
                  {/* Agent badge */}
                  <Box 
                    sx={{
                      position: 'absolute',
                      top: -5,
                      right: -5,
                      bgcolor: 'primary.main',
                      color: 'primary.contrastText',
                      py: 0.5,
                      px: 3,
                      transform: 'rotate(45deg) translateX(20px)',
                      boxShadow: 2,
                      zIndex: 1
                    }}
                  >
                    <Typography variant="caption" fontWeight="bold">AGENT</Typography>
                  </Box>
                  
                  <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, alignItems: { xs: 'flex-start', md: 'center' } }}>
                    <Box sx={{ 
                      display: 'flex', 
                      flexDirection: { xs: 'column', sm: 'row' },
                      alignItems: { xs: 'flex-start', sm: 'center' }, 
                      gap: 2,
                      flex: 1,
                      width: '100%'
                    }}>
                      <Avatar 
                        sx={{ 
                          bgcolor: 'primary.main',
                          width: 50,
                          height: 50
                        }}
                      >
                        <PersonIcon fontSize="large" />
                      </Avatar>
                      
                      <Box sx={{ mt: { xs: 1, sm: 0 } }}>
                        <Typography variant="h6" fontWeight="bold">
                          You're Connected with {agent.name}!
                        </Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                          Your dedicated real estate agent at {agent.businessName}
                        </Typography>
                        
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: 1 }}>
                          <Chip 
                            label="Personal Agent" 
                            size="small" 
                            icon={<PersonIcon fontSize="small" />}
                            sx={{ 
                              bgcolor: theme => alpha(theme.palette.primary.main, 0.1),
                              borderColor: 'primary.main',
                              mb: 0.5
                            }}
                            variant="outlined"
                          />
                          <Chip 
                            label="Pro Access" 
                            size="small" 
                            icon={<StarIcon fontSize="small" />}
                            sx={{ 
                              bgcolor: theme => alpha(theme.palette.success.main, 0.1),
                              borderColor: 'success.main',
                              mb: 0.5
                            }}
                            variant="outlined"
                          />
                          <Chip 
                            label="Expert Guidance" 
                            size="small" 
                            icon={<BusinessIcon fontSize="small" />}
                            sx={{ 
                              bgcolor: theme => alpha(theme.palette.info.main, 0.1),
                              borderColor: 'info.main',
                              mb: 0.5
                            }}
                            variant="outlined"
                          />
                        </Box>
                        
                        {/* Agent contact info */}
                        <Box sx={{ mt: 2, display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                          <Typography variant="caption" color="text.secondary" sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                            <EmailIcon fontSize="small" />
                            {agent.email}
                          </Typography>
                          {agent.phone && (
                            <Typography variant="caption" color="text.secondary" sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                              <PhoneIcon fontSize="small" />
                              {agent.phone}
                            </Typography>
                          )}
                        </Box>
                      </Box>
                    </Box>
                    
                    <Box sx={{ mt: { xs: 3, md: 0 }, ml: { md: 2 }, alignSelf: { xs: 'center', md: 'flex-start' } }}>
                      <Button
                        variant="contained"
                        color="primary"
                        size="large"
                        onClick={() => window.location.href = `/agent/${encodeURIComponent(agent.email)}`}
                        startIcon={<PersonIcon />}
                        sx={{ 
                          fontWeight: 'bold',
                          px: 3,
                          boxShadow: 3,
                          '&:hover': {
                            boxShadow: 6,
                          }
                        }}
                      >
                        View Agent Profile
                      </Button>
                    </Box>
                  </Box>
                </Paper>
              </Grid2>
            ) : user.subscription_tier !== 'pro' ? (
              // Premium Upgrade Banner (existing code for non-pro users without agent)
              <Grid2 size={12} sx={{ mt: 3 }}>
                <Paper 
                  sx={{ 
                    p: { xs: 2, sm: 3 },
                    borderRadius: 1,
                    background: theme => `linear-gradient(145deg, 
                      ${alpha(theme.palette.primary.main, 0.1)} 0%, 
                      ${alpha(theme.palette.secondary.main, 0.2)} 100%)`,
                    border: theme => `1px solid ${alpha(theme.palette.primary.main, 0.3)}`,
                    position: 'relative',
                    overflow: 'hidden'
                  }}
                >
                  {/* Premium badge */}
                  <Box 
                    sx={{
                      position: 'absolute',
                      top: -5,
                      right: -5,
                      bgcolor: 'warning.main',
                      color: 'warning.contrastText',
                      py: 0.5,
                      px: 3,
                      transform: 'rotate(45deg) translateX(20px)',
                      boxShadow: 2,
                      zIndex: 1
                    }}
                  >
                    <Typography variant="caption" fontWeight="bold">PREMIUM</Typography>
                  </Box>
                  
                  <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, alignItems: { xs: 'flex-start', md: 'center' } }}>
                    <Box sx={{ 
                      display: 'flex', 
                      flexDirection: { xs: 'column', sm: 'row' },
                      alignItems: { xs: 'flex-start', sm: 'center' }, 
                      gap: 2,
                      flex: 1,
                      width: '100%'
                    }}>
                      <Avatar 
                        sx={{ 
                          bgcolor: 'warning.main',
                          width: 50,
                          height: 50
                        }}
                      >
                        <StarIcon fontSize="large" />
                      </Avatar>
                      
                      <Box sx={{ mt: { xs: 1, sm: 0 } }}>
                        <Typography variant="h6" fontWeight="bold">
                          Upgrade to CribCritic Premium
                        </Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                          Unlock exclusive features and enhance your home search
                        </Typography>
                        
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: 1 }}>
                          {['Priority Support', '30 Monthly Walkthroughs', '100 Saved Homes'].map((feature) => (
                            <Chip 
                              key={feature}
                              label={feature} 
                              size="small" 
                              icon={<StarIcon fontSize="small" />}
                              sx={{ 
                                bgcolor: theme => alpha(theme.palette.warning.main, 0.1),
                                borderColor: 'warning.main',
                                mb: 0.5
                              }}
                              variant="outlined"
                            />
                          ))}
                        </Box>
                      </Box>
                    </Box>
                    
                    <Box sx={{ mt: { xs: 3, md: 0 }, ml: { md: 2 }, alignSelf: { xs: 'center', md: 'flex-start' } }}>
                      <Button
                        variant="contained"
                        color="warning"
                        size="large"
                        onClick={handleUpgradeToPremium}
                        startIcon={<ArrowUpwardIcon />}
                        sx={{ 
                          fontWeight: 'bold',
                          px: 3,
                          boxShadow: 3,
                          '&:hover': {
                            boxShadow: 6,
                          }
                        }}
                      >
                        Upgrade Now
                      </Button>
                    </Box>
                  </Box>
                </Paper>
              </Grid2>
            ) : (
              // Pro Tier Customer Portal Section (existing code for Pro users)
              <Grid2 size={12} sx={{ mt: 3 }}>
                <Paper 
                  sx={{ 
                    p: { xs: 2, sm: 3 },
                    borderRadius: 1,
                    background: theme => `linear-gradient(145deg, 
                      ${alpha(theme.palette.success.main, 0.1)} 0%, 
                      ${alpha(theme.palette.primary.main, 0.2)} 100%)`,
                    border: theme => `1px solid ${alpha(theme.palette.success.main, 0.3)}`,
                    position: 'relative',
                    overflow: 'hidden'
                  }}
                >
                  {/* Pro badge */}
                  <Box 
                    sx={{
                      position: 'absolute',
                      top: -5,
                      right: -5,
                      bgcolor: 'success.main',
                      color: 'success.contrastText',
                      py: 0.5,
                      px: 3,
                      transform: 'rotate(45deg) translateX(20px)',
                      boxShadow: 2,
                      zIndex: 1
                    }}
                  >
                    <Typography variant="caption" fontWeight="bold">PRO</Typography>
                  </Box>
                  
                  <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, alignItems: { xs: 'flex-start', md: 'center' } }}>
                    <Box sx={{ 
                      display: 'flex', 
                      flexDirection: { xs: 'column', sm: 'row' },
                      alignItems: { xs: 'flex-start', sm: 'center' }, 
                      gap: 2,
                      flex: 1,
                      width: '100%'
                    }}>
                      <Avatar 
                        sx={{ 
                          bgcolor: 'success.main',
                          width: 50,
                          height: 50
                        }}
                      >
                        <StarIcon fontSize="large" />
                      </Avatar>
                      
                      <Box sx={{ mt: { xs: 1, sm: 0 } }}>
                        <Typography variant="h6" fontWeight="bold">
                          CribCritic Pro Membership
                        </Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                          Manage your subscription, billing, and payment information
                        </Typography>
                        
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: 1 }}>
                          {['Priority Support', 'Pro Features', 'Invoice Management'].map((feature) => (
                            <Chip 
                              key={feature}
                              label={feature} 
                              size="small" 
                              icon={<StarIcon fontSize="small" />}
                              sx={{ 
                                bgcolor: theme => alpha(theme.palette.success.main, 0.1),
                                borderColor: 'success.main',
                                mb: 0.5
                              }}
                              variant="outlined"
                            />
                          ))}
                        </Box>
                      </Box>
                    </Box>
                    
                    <Box sx={{ mt: { xs: 3, md: 0 }, ml: { md: 2 }, alignSelf: { xs: 'center', md: 'flex-start' } }}>
                      <Button
                        variant="contained"
                        color="success"
                        size="large"
                        onClick={() => {
                          const isProduction = window.location.hostname === 'cribcritic.ai';
                          const portalUrl = isProduction 
                            ? "https://customer-portal.paddle.com/cpl_01jsnfbjyjf83ph2btrmbd1yqg"
                            : "https://sandbox-customer-portal.paddle.com/cpl_01jtbkhz8mr7qcy9qa6tnbkssb";
                          window.open(portalUrl, '_blank');
                        }}
                        startIcon={<AccountCircleIcon />}
                        sx={{ 
                          fontWeight: 'bold',
                          px: 3,
                          boxShadow: 3,
                          '&:hover': {
                            boxShadow: 6,
                          }
                        }}
                      >
                        Customer Portal
                      </Button>
                    </Box>
                  </Box>
                </Paper>
              </Grid2>
            )}

            {/* Action Buttons */}
            <Grid2 size={12} sx={{ mt: 2 }}>
              <Box sx={{ 
                display: 'flex', 
                flexDirection: { xs: 'column', sm: 'row' },
                justifyContent: { xs: 'flex-start', sm: 'space-between' },
                width: '100%'
              }}>
                {/* Group the first two buttons */}
                <Box sx={{ 
                  display: 'flex', 
                  flexDirection: { xs: 'column', sm: 'row' },
                  gap: 2
                }}>
                  <Button 
                    variant="outlined"
                    onClick={() => setSnackbarOpen(true)}
                    sx={{ borderRadius: 1, whiteSpace: 'nowrap' }}
                  >
                    Download My Data
                  </Button>
                  <Button 
                    variant="contained"
                    onClick={() => setSnackbarOpen(true)}
                    sx={{ borderRadius: 1, whiteSpace: 'nowrap' }}
                  >
                    Update Profile
                  </Button>
                </Box>
                
                {/* Delete button stands alone */}
                <Button 
                  variant="outlined" 
                  color="error"
                  onClick={() => setDeleteDialogOpen(true)}
                  sx={{ 
                    borderRadius: 1,
                    whiteSpace: 'nowrap',
                    mt: { xs: 2, sm: 0 }
                  }}
                >
                  Delete Account
                </Button>
              </Box>
            </Grid2>
          </Grid2>
        </Card>
      </Container>

      {/* Delete Account Confirmation Dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={() => !isDeleting && setDeleteDialogOpen(false)}
      >
        <DialogTitle>Delete Your Account?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            This action cannot be undone. All of your data, including saved homes and conversations, will be permanently deleted.
          </DialogContentText>
          {errorMessage && (
            <Alert severity="error" sx={{ mt: 2 }}>
              {errorMessage}
            </Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button 
            onClick={() => setDeleteDialogOpen(false)} 
            disabled={isDeleting}
          >
            Cancel
          </Button>
          <Button 
            onClick={handleDeleteAccount} 
            color="error" 
            variant="contained"
            disabled={isDeleting}
            startIcon={isDeleting ? <CircularProgress size={20} color="inherit" /> : <DeleteForeverIcon />}
            sx={{ 
              bgcolor: 'error.main',
              '&:hover': {
                bgcolor: 'error.dark',
              },
              fontWeight: 'bold'
            }}
          >
            {isDeleting ? 'Deleting...' : 'Delete Account'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Manual Preferences Dialog */}
      <HomePreferencesDialog
        open={preferencesDialogOpen}
        onClose={() => setPreferencesDialogOpen(false)}
        currentPreferences={user?.home_preferences || ''}
        onSave={updateHomePreferences}
      />

      {/* AI Preferences Wizard */}
      <PreferencesWizardChat
        open={preferencesWizardOpen}
        onClose={() => setPreferencesWizardOpen(false)}
        onComplete={handleWizardComplete}
        autoStart={true}
      />

      {/* This or That Game Dialog */}
      <Dialog
        open={thisOrThatGameOpen}
        onClose={() => setThisOrThatGameOpen(false)}
        fullScreen
        PaperProps={{
          sx: {
            bgcolor: 'background.default',
            // Ensure the dialog covers absolutely everything
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            m: 0,
            maxHeight: '100%',
            maxWidth: '100%',
            borderRadius: 0,
          }
        }}
        slotProps={{
          backdrop: {
            sx: {
              bgcolor: 'background.default',
            }
          }
        }}
        // Ensure highest z-index to cover chat FAB and other elements
        sx={{
          zIndex: (theme) => theme.zIndex.modal + 10,
        }}
      >
        <ThisOrThatGame
          onComplete={handleGameComplete}
          onClose={() => setThisOrThatGameOpen(false)}
          onAbandon={() => setThisOrThatGameOpen(false)}
          showCloseButton={true}
        />
      </Dialog>

      {/* Feature notification Snackbar */}
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={6000}
        onClose={() => setSnackbarOpen(false)}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <Alert 
          onClose={() => setSnackbarOpen(false)} 
          severity="info"
          sx={{ borderRadius: 1 }}
        >
          This feature is coming soon!
        </Alert>
      </Snackbar>
      
      {/* Preferences Update Message */}
      <Snackbar
        open={!!preferencesMessage.text}
        autoHideDuration={6000}
        onClose={() => setPreferencesMessage({ ...preferencesMessage, text: '' })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert 
          onClose={() => setPreferencesMessage({ ...preferencesMessage, text: '' })} 
          severity={preferencesMessage.type as "success" | "error"}
          sx={{ borderRadius: 1 }}
        >
          {preferencesMessage.text}
        </Alert>
      </Snackbar>
    </BasePage>
  );
}

export default Profile;